import {createAuthenticatedApiClient} from './apiClient';
import {mergeTutorialEstado, TUTORIAL_MODULE_KEY_SET} from '@constants/tutorialModules';

const tutorialAPI = createAuthenticatedApiClient();

const extractRows = payload => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    const candidates = [
        payload.items,
        payload.data,
        payload.rows,
        payload.records,
        payload.results,
        payload.tutoriales,
        payload.progress,
    ];
    for (const c of candidates) {
        if (Array.isArray(c)) {
            return c;
        }
    }
    return [];
};

const isCompletedValue = value => {
    if (value === true || value === 1 || value === 'true' || value === '1') {
        return true;
    }
    if (value === false || value === 0 || value === 'false' || value === '0') {
        return false;
    }
    return null;
};

const rowModuloKey = row =>
    row?.modulo
    ?? row?.submodule_key
    ?? row?.submoduleKey
    ?? row?.key
    ?? '';

const META_RESPONSE_KEYS = new Set([
    'message',
    'success',
    'ok',
    'status',
    'error',
    'count',
    'total',
    'meta',
    'pagination',
]);

const NESTED_STATE_KEYS = ['data', 'estado', 'tutoriales', 'progress', 'result'];

const parseModuleCompletion = value => {
    if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
        return isCompletedValue(value);
    }

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const fromSeen = isCompletedValue(value.completado)
        ?? isCompletedValue(value.completed)
        ?? isCompletedValue(value.visto)
        ?? isCompletedValue(value.seen);

    if (fromSeen != null) {
        return fromSeen;
    }

    if (Number(value.last_step ?? value.lastStep ?? 0) > 0) {
        return true;
    }

    if (value.completed_at ?? value.completedAt) {
        return true;
    }

    return null;
};

const extractModuleMap = candidate => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        return null;
    }

    const map = {};
    let catalogMatches = 0;

    for (const [key, value] of Object.entries(candidate)) {
        if (!key || META_RESPONSE_KEYS.has(key) || NESTED_STATE_KEYS.includes(key)) {
            continue;
        }

        const completed = parseModuleCompletion(value);
        if (completed == null) {
            continue;
        }

        map[key] = completed;
        if (TUTORIAL_MODULE_KEY_SET.has(key)) {
            catalogMatches += 1;
        }
    }

    if (!catalogMatches) {
        return null;
    }

    return map;
};

/**
 * Normaliza GET /tutoriales/estado → { [modulo]: boolean }.
 * `true` = completado/visto; `false` = pendiente.
 */
export const normalizeEstadoTutoriales = payload => {
    if (!payload) {
        return {};
    }

    for (const nestedKey of NESTED_STATE_KEYS) {
        const fromNested = extractModuleMap(payload[nestedKey]);
        if (fromNested) {
            return fromNested;
        }
    }

    const fromRoot = extractModuleMap(payload);
    if (fromRoot) {
        return fromRoot;
    }

    const rows = extractRows(payload);
    if (rows.length) {
        const map = {};
        for (const row of rows) {
            const key = rowModuloKey(row);
            if (!key) {
                continue;
            }
            map[key] =
                isCompletedValue(row.completado)
                ?? isCompletedValue(row.completed)
                ?? isCompletedValue(row.visto)
                ?? isCompletedValue(row.seen)
                ?? (Number(row.last_step ?? row.lastStep ?? 0) > 0)
                ?? Boolean(row.completed_at ?? row.completedAt);
        }
        return map;
    }

    return {};
};

export const tutorialService = {
    /**
     * Estrategia A — estado global por clínica.
     * @returns {Promise<Record<string, boolean>>}
     */
    async getEstado() {
        const {data} = await tutorialAPI.get('/tutoriales/estado');
        return mergeTutorialEstado(normalizeEstadoTutoriales(data));
    },

    /**
     * Estrategia A — marcar módulo como completado.
     * @param {string} modulo Clave con guión, p. ej. `dashboard-operativo`
     */
    async completar(modulo) {
        const {data} = await tutorialAPI.post('/tutoriales/completar', {modulo});
        return data;
    },
};
